import _ from 'lodash';

import {CustomWeaveTypePayload} from '../../../typeViews/customWeaveType.types';
import {
  KeyedDictType,
  TraceCallSchema,
} from '../../wfReactInterface/traceServerClientTypes';
import {
  ChatCompletion,
  ChatRequest,
  Choice,
  ContentMessage,
  InternalMessage,
  Message,
  MessagePart,
  ToolCall,
  Usage,
} from '../types';
import {hasStringProp, isMessage} from './utils';

interface OpenAIResponseRequest {
  self: string;
  input: string | Message[];
  model: string;
  instructions?: string;
}

interface ResponseUserMessage {
  role: 'user';
  content: string;
}
interface ResponseTextMessage {
  annotations: any[];
  text: string;
  type: 'text' | 'input_text' | 'output_text';
}

type ExternalResponseAudioMessage = ReasoningWrapper<{
  type: 'audio' | 'input_audio' | 'output_audio';
  audio: CustomWeaveTypePayload; // Content CustomWeaveType
  transcript?: string;
}>;

type InternalResponseAudioMessage = ReasoningWrapper<
  ContentMessage<'audio'>
> & {
  transcript?: string;
};

interface ResponseMessage {
  id: string;
  content: (ExternalResponseAudioMessage | ResponseTextMessage)[];
  role: string;
  type: 'message';
}

interface ResponseFunctionCall {
  arguments: string;
  call_id: string;
  id: string;
  name: string;
  status: string;
  type: 'function_call';
}

interface ResponseFunctionCallOutput {
  type: 'function_call_output';
  call_id: string;
  output: any;
}

// Minimal MCP call shape from Realtime Audio MCP integration
interface ResponseMCPCall {
  id: string;
  type: 'mcp_call';
  name: string;
  arguments: string;
  // Inline output may be present on the same item
  output?: any;
  // Optional metadata we ignore for rendering
  server_label?: string;
  approval_request_id?: string | null;
  error?: any;
}

// Some servers may emit a list tools message ahead of calls; treat as ignorable
interface ResponseMCPListTools {
  id: string;
  type: 'mcp_list_tools';
  server_label?: string;
  tools: any[];
}

// Reasoning envelope emitted ahead of a 'message'
interface ResponseReasoning {
  type: 'reasoning';
  summary?: Array<{text?: string}>;
}

type _OpenAIResponseMessage =
  | ResponseFunctionCall
  | ResponseMessage
  | ResponseFunctionCallOutput
  | ResponseMCPCall
  | ResponseMCPListTools
  | ResponseReasoning;

type ReasoningWrapper<T> = T & {reasoning_content?: string};
type OpenAIResponseMessage = ReasoningWrapper<_OpenAIResponseMessage>;

export const isOpenAIResponseMessage = (
  value: any
): value is OpenAIResponseMessage => {
  if (!_.isPlainObject(value)) return false;

  // {role: ${role}, content: str} format message
  if (hasStringProp(value, 'content') && hasStringProp(value, 'role')) {
    return true;
  }

  if (!hasStringProp(value, 'type')) {
    return false;
  }

  const t = value.type;

  if (t === 'function_call') {
    // Validate ResponseFunctionCall
    return (
      hasStringProp(value, 'id') &&
      hasStringProp(value, 'name') &&
      hasStringProp(value, 'arguments') &&
      hasStringProp(value, 'call_id') &&
      hasStringProp(value, 'status')
    );
  }

  if (t === 'function_call_output') {
    // Validate ResponseFunctionCallOutput
    return 'output' in value && hasStringProp(value, 'call_id');
  }

  if (t === 'mcp_call') {
    // Validate minimal MCP call
    return (
      hasStringProp(value, 'id') &&
      hasStringProp(value, 'name') &&
      hasStringProp(value, 'arguments')
    );
  }

  if (t === 'mcp_list_tools') {
    // Validate minimal mcp_list_tools envelope
    return 'tools' in value && Array.isArray(value.tools);
  }

  if (t === 'message') {
    // Validate ResponseMessage
    if (!hasStringProp(value, 'id')) return false;
    if (!hasStringProp(value, 'role')) return false;
    if (!('content' in value && _.isArray(value.content))) return false;

    const isTextPart = (part: any) =>
      _.isPlainObject(part) &&
      hasStringProp(part, 'type') &&
      (part.type === 'text' ||
        part.type === 'input_text' ||
        part.type === 'output_text') &&
      hasStringProp(part, 'text');

    const isAudioPart = (part: any) =>
      _.isPlainObject(part) &&
      hasStringProp(part, 'type') &&
      (part.type === 'audio' ||
        part.type === 'input_audio' ||
        part.type === 'output_audio') &&
      'audio' in part &&
      _.isPlainObject(part.audio);

    const content = value.content as any[];
    return content.every(p => isTextPart(p) || isAudioPart(p));
  }

  if (t === 'reasoning') {
    return true;
  }

  return false;
};

interface OpenAIResponseResult {
  id: string;
  model: string;
  output: ResponseFunctionCall[];
  tools: ResponseFunctionCall[];
  usage: Usage;
  status: string;
  created_at: number;
  instructions?: string;
  object: 'response';
}

const responseFunctionCallToToolCall = (
  functionCall: ResponseFunctionCall,
  output?: ResponseFunctionCallOutput
): ToolCall => {
  const call = {
    id: functionCall.call_id,
    type: 'function',
    function: {
      name: functionCall.name,
      arguments: functionCall.arguments,
    },
  };
  if (output) {
    return {
      ...call,
      response: {
        role: 'tool',
        content:
          typeof output.output === 'string'
            ? output.output
            : JSON.stringify(output.output),
        tool_call_id: output.call_id,
      },
    };
  }
  return call;
};

const responseMCPCallToToolCall = (mcpCall: ResponseMCPCall): ToolCall => {
  const call: ToolCall = {
    id: mcpCall.id,
    type: 'function',
    function: {
      name: mcpCall.name,
      arguments: mcpCall.arguments,
    },
  };
  if (mcpCall.output !== undefined && mcpCall.output !== null) {
    return {
      ...call,
      response: {
        role: 'tool',
        content:
          typeof mcpCall.output === 'string'
            ? mcpCall.output
            : JSON.stringify(mcpCall.output),
        tool_call_id: mcpCall.id,
      },
    };
  }
  return call;
};

export const responseMessageToMessage = (
  message: OpenAIResponseMessage & {reasoning_content?: string}
): Message | undefined => {
  if (message['type'] === 'message') {
    const {role, content} = message;
    const mappedContent: MessagePart[] = (content ?? []).flatMap<MessagePart>(
      part => {
        if (
          part.type === 'input_audio' ||
          part.type === 'output_audio' ||
          part.type === 'audio'
        ) {
          const internalMessage: InternalResponseAudioMessage = {
            type: 'audio',
            audio: part.audio,
            transcript: part.transcript,
            reasoning_content: message?.reasoning_content,
          };
          return [internalMessage as MessagePart];
        }
        if (
          part.type === 'input_text' ||
          part.type === 'output_text' ||
          part.type === 'text'
        ) {
          const internalMessage: InternalMessage = {
            type: 'text',
            text: part.text,
          };
          return [internalMessage as MessagePart];
        }
        return [];
      }
    );

    const msg: Message = {
      role: role,
      content: mappedContent,
    };
    if ('reasoning_content' in message) {
      msg['reasoning_content'] = message.reasoning_content;
    }
    return msg;
  }
  return undefined;
};
export const responseOutputMessagesToChoices = (
  messages: OpenAIResponseMessage[]
): Choice[] => {
  // Preprocess reasoning entries: attach to the following message and remove
  const preprocessed: OpenAIResponseMessage[] = [];
  for (let i = 0; i < messages.length; i++) {
    const m = messages[i];
    if (m && m.type === 'reasoning') {
      // Join all available summary texts with newlines (handle arrays)
      const summaryText = _.isArray(m?.summary)
        ? m.summary
            .map((s: any) => (s && _.isString(s.text) ? s.text : ''))
            .join('\n\n')
        : null;
      if (_.isString(summaryText)) {
        // Find the next output item that we render (message or function/mcp call)
        for (let j = i + 1; j < messages.length; j++) {
          const next: OpenAIResponseMessage & {reasoning_content?: string} =
            messages[j];
          if (next) {
            next.reasoning_content = summaryText;
            break;
          }
        }
      }
      // Skip adding this 'reasoning' item to the output list
      continue;
    }
    preprocessed.push(messages[i]);
  }

  // Pair function_call with function_call_output by call_id
  const outputsByCallId = new Map<
    string,
    ReasoningWrapper<ResponseFunctionCallOutput>
  >();
  for (const m of preprocessed) {
    if (m.type === 'function_call_output') {
      outputsByCallId.set(m.call_id, m);
    }
  }

  // Help TypeScript infer the correct element type for flatMap
  return preprocessed.flatMap<Choice>((message, index) => {
    if (message.type === 'function_call') {
      const paired = outputsByCallId.get(message.call_id);
      const toolCall = responseFunctionCallToToolCall(message, paired);
      const reasoningAttached =
        message.reasoning_content || paired?.reasoning_content;
      const choice: Choice = {
        index,
        finish_reason: 'stop',
        message: {
          role: 'assistant',
          content: '',
          tool_calls: [toolCall],
          // Propagate any attached reasoning from the preprocessor
          reasoning_content: reasoningAttached,
        } as Message,
      };
      return [choice];
    }
    if (message.type === 'mcp_call') {
      const toolCall = responseMCPCallToToolCall(message);
      const choice: Choice = {
        index,
        finish_reason: 'stop',
        message: {
          role: 'assistant',
          content: '',
          tool_calls: [toolCall],
          reasoning_content: message.reasoning_content,
        } as Message,
      };
      return [choice];
    }
    if (message.type === 'function_call_output') {
      // Already paired above; skip emitting a separate choice
      return [];
    }
    if (message.type === 'mcp_list_tools') {
      // Ignore tool listings in chat rendering
      return [];
    }
    if (message.type === 'message') {
    }
    // type === 'message'
    const msg: Message =
      responseMessageToMessage(message) ??
      ({
        role: 'assistant',
        content: '',
      } as Message);

    const choice: Choice = {
      index,
      finish_reason: 'stop',
      message: msg,
    };
    return [choice];
  });
};

export const normalizeOAIReponsesResult = (
  result: OpenAIResponseResult
): ChatCompletion => {
  const choices = responseOutputMessagesToChoices(result.output);
  return {
    id: result.id,
    model: result.model,
    created: result.created_at,
    usage: result.usage,
    choices,
    system_fingerprint: '',
  };
};
export const normalizeOAIResponsesRequest = (request: any): ChatRequest => {
  const input =
    'input' in request
      ? request['input']
      : 'messages' in request
        ? request['messages']
        : [];

  let messages: Message[] = [];
  if (_.isString(input)) {
    messages = [
      {
        role: 'user',
        content: input,
      },
    ];
  } else if (_.isArray(input)) {
    // Preprocess reasoning entries: concatenate their summaries and attach to the following output
    // item regardless of type (message, function_call, mcp_call)
    const preprocessed: any[] = [];
    let pendingReasoning: string | null = null;
    for (let i = 0; i < input.length; i++) {
      const item: any = input[i];
      if (item && item.type === 'reasoning') {
        const reasoningText = _.isArray(item?.summary)
          ? item.summary
              .map((s: any) => (s && _.isString(s.text) ? s.text : ''))
              .join('\n\n')
          : null;
        if (_.isString(reasoningText) && reasoningText.length > 0) {
          pendingReasoning = pendingReasoning
            ? `${pendingReasoning}\n${reasoningText}`
            : reasoningText;
        }
        continue; // Do not push raw reasoning items
      }
      // Attach to next non reasoning block
      // TODO(zbirenbaum): Research if there ever a case where we would get two reasoning blocks in a row.
      if (item && pendingReasoning && item.type !== 'reasoning') {
        item.reasoning_content = pendingReasoning;
        pendingReasoning = null;
      }
      preprocessed.push(item);
    }

    messages = preprocessed.flatMap<Message>(
      (msg: ResponseUserMessage | OpenAIResponseMessage) => {
        if ('type' in msg && msg['type'] === 'function_call') {
          const output = preprocessed.find(maybe_output => {
            return (
              maybe_output.type === 'function_call_output' &&
              msg.call_id === maybe_output.call_id
            );
          });
          const tool_call = responseFunctionCallToToolCall(msg, output);
          const m: Message = {
            role: 'assistant',
            tool_calls: [tool_call],
            reasoning_content:
              msg?.reasoning_content || output?.reasoning_content,
          };
          return m;
        } else if ('type' in msg && msg['type'] === 'mcp_call') {
          const tool_call = responseMCPCallToToolCall(msg);
          const m: Message = {
            role: 'assistant',
            tool_calls: [tool_call],
            reasoning_content: msg?.reasoning_content,
          };
          return m;
        } else if ('type' in msg && msg['type'] === 'function_call_output') {
          return [];
        } else if ('type' in msg && msg['type'] === 'mcp_list_tools') {
          return [];
        } else if ('type' in msg) {
          const result = responseMessageToMessage(msg);
          return result ? result : [];
        } else if ('role' in msg && 'content' in msg) {
          return msg;
        }
        return [];
      }
    );
  }
  if ('instructions' in request) {
    return {
      messages: [
        {
          role: 'system',
          content: request['instructions'],
        },
        ...messages,
      ],
      model: request['model'],
    };
  }
  return {
    messages,
    model: request['model'],
  };
};
export const isTraceCallChatFormatOAIResponses = (
  call: TraceCallSchema
): boolean => {
  return (
    isTraceCallChatFormatOAIResponsesRequest(call.inputs) &&
    (call.output == null ||
      isTraceCallChatFormatOAIResponsesResult(call.output))
  );
};

export const isTraceCallChatFormatOAIResponsesResult = (
  outputs: any
): outputs is OpenAIResponseResult => {
  if (!_.isPlainObject(outputs)) {
    return false;
  }
  const hasOutput = 'output' in outputs && _.isArray(outputs['output']);
  if (hasOutput) {
    return outputs['output'].every(isOpenAIResponseMessage);
  }
  return 'object' in outputs && outputs['object'] === 'response';
};

export const isTraceCallChatFormatOAIResponsesRequest = (
  value: KeyedDictType
): value is OpenAIResponseRequest => {
  const hasMessages = 'messages' in value && _.isArray(value['messages']);
  if (hasMessages) {
    return value['messages'].every(isOpenAIResponseMessage);
  }
  const hasInput =
    'input' in value &&
    (_.isString(value['input']) || _.isArray(value['input']));
  const hasModel = 'model' in value && _.isString(value['model']);
  const hasSelf = 'self' in value && _.isString(value['self']);
  return (hasInput || hasMessages) && hasModel && hasSelf;
};

export const isTraceCallChatFormatOpenAI = (call: TraceCallSchema): boolean => {
  // TODO: This is probably not a good enough check
  // We should do legitimate schema validation on this
  const {messages} = call.inputs;
  if (!_.isArray(messages)) {
    return false;
  }
  if (
    hasStringProp(call.inputs, 'model') &&
    call.inputs.model.toLowerCase().includes('mistral')
  ) {
    return false;
  }
  return messages.every(isMessage);
};
